import React, { useState, useEffect } from "react";
import { errMessage } from "../lib/errors";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, LogOut, User as UserIcon, Loader2, Shield, Gift, RefreshCw, Copy, Check, Award, CalendarCheck, ChefHat } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useToast } from "../hooks/use-toast";
import { menuLookupKeys, menuHighlights } from "../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Build a lookup_key → mock.js item map so "Order Again" restores real names + images.
const KEY_TO_ITEM = (() => {
  const map = {};
  Object.entries(menuLookupKeys || {}).forEach(([id, key]) => {
    const item = menuHighlights.find((m) => String(m.id) === String(id));
    if (item) map[key] = item;
  });
  return map;
})();

export default function Account() {
  const { user, isAuthenticated, loading, updateProfile, logout, authHeaders, isAdmin, canStaff } = useAuth();
  const { add, setOpen } = useCart();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [address, setAddress] = useState(user?.address || "");
  const [postcode, setPostcode] = useState(user?.postcode || "");
  const [birthday, setBirthday] = useState(
    user?.birthday ? user.birthday.split("-").reverse().join("/") : ""
  );
  const [saving, setSaving] = useState(false);
  const [orders, setOrders] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!isAuthenticated) return;
    axios.get(`${API}/orders/mine`, { headers: authHeaders }).then((r) => setOrders(r.data.items || [])).catch(() => {});
    axios.get(`${API}/reservations/mine`, { headers: authHeaders }).then((r) => setReservations(r.data.items || [])).catch(() => {});
    axios.get(`${API}/loyalty/me`, { headers: authHeaders }).then((r) => setLoyalty(r.data)).catch(() => {});
    axios.get(`${API}/referral/mine`, { headers: authHeaders }).then((r) => setReferral(r.data)).catch(() => {});
  }, [isAuthenticated, authHeaders]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--mm-bg)] flex items-center justify-center">
        <Loader2 size={32} className="text-[var(--mm-accent)] animate-spin" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login?next=/account" replace />;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({ name, phone, address, postcode, birthday });
      toast({ title: "Profile saved", description: "Your details will pre-fill on future orders." });
    } catch (err) {
      toast({ title: "Save failed", description: errMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  const reorder = (order) => {
    const items = order.items || [];
    if (items.length === 0) {
      toast({ title: "Nothing to reorder", description: "This order didn't record its line items." });
      return;
    }
    items.forEach((it) => {
      const menuItem = KEY_TO_ITEM[it.lookup_key];
      const name = menuItem?.name || it.name || it.lookup_key.replace(/^mamamina_/, "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      for (let i = 0; i < it.quantity; i++) {
        add({
          lookup_key: it.lookup_key,
          name,
          price: (it.unit_amount || 0) / 100,
          image: menuItem?.image || null,
        });
      }
    });
    setOpen(true);
    toast({ title: `${items.length} item${items.length !== 1 ? "s" : ""} added`, description: "Ready to reorder — head to checkout when you're set." });
  };

  const copyRef = async () => {
    if (!referral) return;
    await navigator.clipboard.writeText(referral.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="min-h-screen bg-[var(--mm-bg)] grain">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-14">
        <Link to="/" className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors mb-10">
          <ArrowLeft size={14} /> Back to Home
        </Link>

        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="section-label mb-3">My Account</div>
            <h1 className="font-display italic text-5xl md:text-6xl text-[var(--mm-cream)] leading-tight">
              Hello, <span className="text-[var(--mm-accent)]">{user.name?.split(" ")[0] || "friend"}.</span>
            </h1>
            <p className="text-[var(--mm-cream-muted)] mt-2 text-sm">{user.email}</p>
          </div>
          <button onClick={logout} className="flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors">
            <LogOut size={14} /> Sign Out
          </button>
        </div>

        {(isAdmin || canStaff) && (
          <div className="mb-10 grid grid-cols-1 md:grid-cols-2 gap-3">
            {isAdmin && (
              <Link to="/admin" data-testid="account-admin-link" className="flex items-center gap-3 p-4 border border-[var(--mm-accent)] bg-[var(--mm-bg-alt)] text-[var(--mm-cream)] hover:bg-[var(--mm-accent)] hover:text-[var(--mm-bg)] transition-all duration-300">
                <Shield size={16} />
                <span className="section-label">Admin Dashboard</span>
                <span className="text-xs tracking-[0.2em] uppercase ml-auto">Open →</span>
              </Link>
            )}
            {canStaff && (
              <Link to="/staff" data-testid="account-staff-link" className="flex items-center gap-3 p-4 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] text-[var(--mm-cream)] hover:bg-[var(--mm-accent)] hover:text-[var(--mm-bg)] hover:border-[var(--mm-accent)] transition-all duration-300">
                <ChefHat size={16} />
                <span className="section-label">Back Office</span>
                <span className="text-xs tracking-[0.2em] uppercase ml-auto">Open →</span>
              </Link>
            )}
          </div>
        )}

        {/* Loyalty stamp card */}
        {loyalty && (
          <div className="mb-10 p-6 md:p-8 border border-[var(--mm-accent)] bg-[var(--mm-bg-alt)]">
            <div className="flex items-center gap-3 mb-4">
              <Award size={18} className="text-[var(--mm-accent)]" />
              <span className="section-label">Loyalty</span>
              {loyalty.reward_ready && (
                <span className="text-[0.62rem] tracking-[0.22em] uppercase bg-[var(--mm-accent)] text-[var(--mm-bg)] px-2 py-0.5">Reward Ready</span>
              )}
            </div>
            <h3 className="font-display italic text-2xl md:text-3xl text-[var(--mm-cream)] mb-4 leading-tight">
              {loyalty.reward_ready ? (
                <>You&apos;ve earned <span className="text-[var(--mm-accent)]">{loyalty.reward_percent}% off</span> your next order.</>
              ) : (
                <>{loyalty.target - loyalty.progress} more order{loyalty.target - loyalty.progress !== 1 ? "s" : ""} until <span className="text-[var(--mm-accent)]">{loyalty.reward_percent}% off.</span></>
              )}
            </h3>
            <div className="flex items-center gap-2 mb-2">
              {[...Array(loyalty.target)].map((_, i) => (
                <div key={i} className={`flex-1 h-2 ${i < loyalty.progress ? "bg-[var(--mm-accent)]" : "bg-[var(--mm-line)]"}`} />
              ))}
            </div>
            <div className="text-xs text-[var(--mm-cream-muted)]">{loyalty.progress}/{loyalty.target} orders</div>
          </div>
        )}

        {/* Referral */}
        {referral && (
          <div className="mb-10 p-6 md:p-8 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]">
            <div className="flex items-center gap-3 mb-4">
              <Gift size={16} className="text-[var(--mm-accent)]" />
              <span className="section-label">Refer a friend</span>
            </div>
            <p className="text-[var(--mm-cream-muted)] font-light text-sm mb-4">{referral.reward}</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 px-4 py-3 border border-[var(--mm-line)] text-[var(--mm-cream)] font-mono tracking-widest text-sm overflow-x-auto whitespace-nowrap">
                {referral.url}
              </div>
              <button onClick={copyRef} className="btn-outline inline-flex items-center gap-2 justify-center">
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy Link</>}
              </button>
            </div>
          </div>
        )}

        {/* Past orders + reorder */}
        {orders.length > 0 && (
          <div className="mb-10">
            <div className="section-label mb-4">Recent orders</div>
            <div className="space-y-3">
              {orders.slice(0, 5).map((o) => (
                <div key={o.session_id} className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <div className="font-display italic text-[var(--mm-cream)] text-lg">
                      ${((o.amount || 0) / 100).toFixed(2)} · {(o.items || []).length} item{(o.items || []).length !== 1 ? "s" : ""}
                    </div>
                    <div className="text-xs text-[var(--mm-cream-muted)] tracking-wide">
                      {o.payment_status || o.status} · {o.created_at ? new Date(o.created_at).toLocaleDateString("en-AU") : ""}
                    </div>
                  </div>
                  <button
                    onClick={() => reorder(o)}
                    data-testid={`reorder-btn-${o.session_id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--mm-accent)] text-[var(--mm-accent)] text-xs tracking-[0.24em] uppercase hover:bg-[var(--mm-accent)] hover:text-[var(--mm-bg)] transition-all"
                  >
                    <RefreshCw size={13} /> Order Again
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Past reservations */}
        {reservations.length > 0 && (
          <div className="mb-10" data-testid="my-reservations">
            <div className="section-label mb-4 flex items-center gap-2"><CalendarCheck size={13} /> My reservations</div>
            <div className="space-y-3">
              {reservations.slice(0, 5).map((r) => (
                <div key={r.id} className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid={`my-reservation-${r.id}`}>
                  <div>
                    <div className="font-display italic text-[var(--mm-cream)] text-lg">
                      {r.date} · {r.time || ""} · Party of {r.party_size || "?"}
                    </div>
                    <div className="text-xs text-[var(--mm-cream-muted)] tracking-wide">
                      Status: {r.status || "new"}
                      {r.notes ? ` · "${r.notes}"` : ""}
                    </div>
                  </div>
                  <div className={`text-[0.62rem] tracking-[0.24em] uppercase px-2 py-1 border ${resStatusColor(r.status)}`}>
                    {r.status || "new"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={save} className="p-8 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]">
          <div className="flex items-center gap-3 mb-6">
            <UserIcon size={16} className="text-[var(--mm-accent)]" />
            <span className="section-label">Details</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] mb-1 block">Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-4 py-3 focus:border-[var(--mm-accent)] outline-none" />
            </label>
            <label className="block">
              <span className="text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] mb-1 block">Phone</span>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-4 py-3 focus:border-[var(--mm-accent)] outline-none" />
            </label>
            <label className="block md:col-span-2">
              <span className="text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] mb-1 block">Address</span>
              <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="For faster delivery orders" className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none" />
            </label>
            <label className="block">
              <span className="text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] mb-1 block">Postcode</span>
              <input value={postcode} maxLength={4} onChange={(e) => setPostcode(e.target.value.replace(/[^\d]/g, ""))} className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] px-4 py-3 focus:border-[var(--mm-accent)] outline-none" />
            </label>
            <label className="block">
              <span className="text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] mb-1 block">Birthday DD/MM <span className="normal-case tracking-normal opacity-70">(free dessert in your birthday week)</span></span>
              <input value={birthday} maxLength={5} placeholder="24/07" data-testid="account-birthday-input" onChange={(e) => setBirthday(e.target.value)} className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none" />
            </label>
          </div>
          <button type="submit" disabled={saving} className="btn-primary mt-6 flex items-center justify-center gap-2 disabled:opacity-60">
            {saving && <Loader2 size={14} className="animate-spin" />}
            Save Changes
          </button>
        </form>
      </div>
    </div>
  );
}

function resStatusColor(s) {
  return {
    new: "border-[var(--mm-accent)] text-[var(--mm-accent)]",
    confirmed: "border-green-400 text-green-400",
    seated: "border-[var(--mm-cream)] text-[var(--mm-cream)]",
    cancelled: "border-red-400 text-red-400",
  }[s] || "border-[var(--mm-line)] text-[var(--mm-cream-muted)]";
}
